import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Text,
    VStack,
    HStack,
    Box,
    useToast,
    Icon,
} from "@chakra-ui/react";
import {
    IoCardOutline,
    IoCheckmarkCircle,
    IoArrowForward,
    IoTimeOutline,
} from "react-icons/io5";
import { httpsCallable } from "firebase/functions";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { functions, db } from "../../firebase/firebase-config";
import { getStripeMode } from "../../lib/stripeMode";
import { useState } from "react";

const PAYOUT_POINTS = [
    "Get paid automatically when tickets sell at your venue",
    "Track every booking and payout in one place",
    "Bank-level security — Proximy never stores your details",
];

// Payout setup for venue partners. Same Stripe Connect onboarding hosts go
// through, minus the producer agreement — a venue can list with us without
// ever becoming an event producer, but still needs somewhere for its share
// of ticket revenue to land.
//
// `alreadyConnected` means they've already completed Stripe in another role
// (almost always as a host). Venues pay out through the very same account,
// so there's nothing to redo — we just tell them it's already handled and
// link the existing account to their venue role.
export const VenuePayoutModal = ({
    isOpen,
    onClose,
    user,
    returnPath,
    alreadyConnected = false,
    onLinked,
}) => {
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const handleUseExistingAccount = async () => {
        if (!user) return;

        setLoading(true);
        try {
            await setDoc(
                doc(db, "users", user.uid),
                { isVenueOwner: true, venueOwnerSince: Timestamp.now() },
                { merge: true }
            );
            onLinked?.();
        } catch (error) {
            console.error("❌ Error linking existing Stripe account to venue:", error);
            toast({
                title: "Error",
                description:
                    error.message || "Couldn't link your payout account. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleStripeSetup = async () => {
        if (!user) return;

        setLoading(true);
        try {
            const callable = httpsCallable(functions, "createStripeExpressAccount");
            const { data: result } = await callable({
                userId: user.uid,
                email: user.email,
                mode: getStripeMode(),
                appBaseUrl: window.location.origin,
                role: "venue",
                ...(returnPath ? { returnPath } : {}),
            });

            if (result?.success && result?.onboardingUrl) {
                window.location.href = result.onboardingUrl;
            } else {
                throw new Error("Failed to start payout setup");
            }
        } catch (error) {
            console.error("❌ Error starting venue payout setup:", error);
            toast({
                title: "Error",
                description:
                    error.message || "Failed to start payout setup. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={{ base: "full", md: "md" }}
            isCentered
            scrollBehavior="inside"
        >
            <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
            <ModalContent
                borderRadius={{ base: 0, md: "2xl" }}
                overflow="hidden"
            >
                <ModalHeader
                    pb={2}
                    pt={5}
                    px={6}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                >
                    <HStack spacing={3} align="center">
                        <Box
                            bg="blue.50"
                            p={2}
                            borderRadius="lg"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Icon as={IoCardOutline} color="#508cf7" boxSize={4} />
                        </Box>
                        <Box>
                            <Text fontSize="lg" fontWeight="700" color="gray.800">
                                {alreadyConnected ? "Payouts Already Set Up" : "Set Up Payouts"}
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                                {alreadyConnected
                                    ? "Nothing to do — you're already connected"
                                    : "So your venue gets paid when tickets sell"}
                            </Text>
                        </Box>
                    </HStack>
                    <ModalCloseButton top={4} right={4} />
                </ModalHeader>

                <ModalBody px={6} py={5}>
                    {alreadyConnected ? (
                        <VStack spacing={5} align="stretch">
                            <HStack
                                spacing={3}
                                bg="green.50"
                                border="1px solid"
                                borderColor="green.200"
                                borderRadius="xl"
                                px={4}
                                py={4}
                                align="flex-start"
                            >
                                <Icon
                                    as={IoCheckmarkCircle}
                                    color="green.500"
                                    boxSize={5}
                                    mt="1px"
                                    flexShrink={0}
                                />
                                <Box>
                                    <Text fontSize="sm" fontWeight="600" color="green.800">
                                        Your Stripe account is already verified
                                    </Text>
                                    <Text fontSize="xs" color="green.700" mt={1} lineHeight="1.6">
                                        Venues get paid through the same account you set up
                                        earlier, so there's nothing to redo. Your venue
                                        earnings will land in the exact same place.
                                    </Text>
                                </Box>
                            </HStack>

                            <Text fontSize="11px" color="gray.400" textAlign="center">
                                You can change payout details anytime in your Stripe dashboard.
                            </Text>
                        </VStack>
                    ) : (
                        <VStack spacing={5} align="stretch">
                            <Box
                                bgGradient="linear(to-br, blue.50, white)"
                                border="1px solid"
                                borderColor="blue.100"
                                borderRadius="xl"
                                p={4}
                            >
                                <VStack align="stretch" spacing={2}>
                                    {PAYOUT_POINTS.map((point) => (
                                        <HStack key={point} spacing={2} align="flex-start">
                                            <Icon
                                                as={IoCheckmarkCircle}
                                                color="#508cf7"
                                                boxSize={4}
                                                mt="2px"
                                                flexShrink={0}
                                            />
                                            <Text fontSize="sm" color="gray.700">
                                                {point}
                                            </Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            </Box>

                            <HStack
                                spacing={3}
                                bg="orange.50"
                                border="1px solid"
                                borderColor="orange.200"
                                borderRadius="lg"
                                px={3}
                                py={3}
                                align="flex-start"
                            >
                                <Icon
                                    as={IoTimeOutline}
                                    color="orange.500"
                                    boxSize={4}
                                    mt="2px"
                                    flexShrink={0}
                                />
                                <Box>
                                    <Text fontSize="xs" color="orange.700" fontWeight="600">
                                        Next: Stripe verification
                                    </Text>
                                    <Text fontSize="11px" color="orange.700" mt="1px">
                                        We'll send you to Stripe to verify your business and
                                        bank details. Takes about 2–3 minutes, then you'll come
                                        right back to finish your listing.
                                    </Text>
                                </Box>
                            </HStack>

                            <Text fontSize="11px" color="gray.400" textAlign="center">
                                You don't need to be an event host — this is just for payouts.
                            </Text>
                        </VStack>
                    )}
                </ModalBody>

                <ModalFooter
                    borderTop="1px solid"
                    borderColor="gray.100"
                    py={4}
                    px={6}
                >
                    <Button
                        size="sm"
                        variant="ghost"
                        fontWeight="600"
                        onClick={onClose}
                        mr={2}
                        isDisabled={loading}
                    >
                        Not now
                    </Button>
                    <Button
                        size="sm"
                        bg="#508cf7"
                        color="white"
                        fontWeight="600"
                        borderRadius="lg"
                        px={5}
                        rightIcon={<Icon as={IoArrowForward} boxSize={4} />}
                        _hover={{ bg: "#226cf1" }}
                        onClick={alreadyConnected ? handleUseExistingAccount : handleStripeSetup}
                        isLoading={loading}
                        loadingText={alreadyConnected ? "Continuing…" : "Setting up…"}
                    >
                        {alreadyConnected ? "Continue" : "Continue to Stripe"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
